import { Store } from '@ngrx/store';
import {
  AbstractControlState,
  MarkAsTouchedAction,
  ValidationErrors
} from 'ngrx-forms';
import {
  Observable,
  combineLatest
} from 'rxjs';
import { IDxForm } from './form';
import { map } from 'rxjs/operators';

export interface AbstractControlStateLike {
  id: string | string[];
  isInvalid: boolean;
  errors: ValidationErrors | null;
  isUntouched: boolean;
  [ key: string ]: any;
}

export class StepControl<FormValue> {

  public get invalid() {
    if (this.state.isUntouched) {
      for (const id of this.state.id) {
        this.store$.dispatch(new MarkAsTouchedAction(id));
      }
    }
    return this.state.isInvalid;
  }

  public get valid() {
    return !this.state.isInvalid;
  }

  public get errors() {
    return this.state.errors;
  }

  public state: AbstractControlStateLike;

  public get id() {
    return this.state.id;
  }

  constructor(public store$: Store<any>, public state$: Observable<AbstractControlStateLike>) {
    this.state$.subscribe(state => this.state = {
      id: Array.isArray(state.id) ? state.id : [ state.id ],
      isInvalid: state.isInvalid,
      errors: state.errors,
      isUntouched: state.isUntouched,
    });
  }

  private static mergeStateErrors(a: ValidationErrors | null, b: ValidationErrors | null): ValidationErrors | null {
    if (a === b) {
      return a;
    }
    if (a === null) {
      return b;
    }
    if (b === null) {
      return a;
    }
    return { ...a, ...b };
  };

  public static FromStepControls<T = any>(store$: Store<any>, ...stepControls: Array<StepControl<any>>): StepControl<T> {

    const state$ = combineLatest(...stepControls.map(sc => sc.state$)).pipe(
      map((states: AbstractControlStateLike[]) => states.reduce((acc, state) => ({
        id: [ ...(acc.id as string[]), ...(Array.isArray(state.id) ? state.id : []), ],
        isInvalid: acc.isInvalid || state.isInvalid,
        errors: StepControl.mergeStateErrors(acc.errors, state.errors),
        isUntouched: acc.isUntouched || state.isUntouched,
      }), { id: [], isInvalid: false, errors: null, isUntouched: false }))
    );

    return new StepControl<T>(store$, state$);

  }

  public static FromControls<T = any>(store$: Store<any>, ...controls: Array<IDxForm<any, any>>): StepControl<T> {

    const state$ = combineLatest(...controls.map(control => control.__state$))
      .pipe(
        map((states: AbstractControlState<any>[]) => states.reduce((acc: AbstractControlStateLike, partial: AbstractControlState<any>) => ({
          id:        [ ...(acc.id as string[]), partial.id ],
          isInvalid: acc.isInvalid || partial.isInvalid,
          errors:    StepControl.mergeStateErrors(acc.errors, partial.errors),
          isUntouched: acc.isUntouched || partial.isUntouched
        }), { id: [], isInvalid: false, errors: null, isUntouched: false }))
      );

    return new StepControl<T>(store$, state$);

  }

}
